import type {
  CatalogItem,
  TSourceTrackPageType,
  TPositionInFeed,
  TChatResponseType,
} from '@/types/api';

const MYTRACKER_COUNTER_ID = '3768933';

export type PaywallTrigger = 'manual' | 'chat_limit' | 'feature_lock';
export type SubscriptionPlan = 'free' | 'pro';
export type BillingPeriod = 'monthly' | 'yearly';
export type WardrobeTab = 'my_wardrobe' | 'saved_items' | 'saved_outfits';
export type DigitizeMethod = 'camera' | 'gallery';
export type DigitizeErrorType = 'bad_photo' | 'api_error' | 'timeout';
export type EnhancementType = 'full';
export type EnhanceRejectReason = 'bad_result' | 'changed_mind' | 'other';
export type OnboardingStepName =
  | 'gender_select'
  | 'age_select'
  | 'size_select'
  | 'style_boundaries'
  | 'budget_select'
  | 'style_quiz';
export type QuickMenuAction = 'gallery' | 'wardrobe' | 'favorites';

type TmrPayload = Record<string, unknown> & {
  typeEvent?: 'action' | 'screen_view' | 'conversion' | 'click';
};

const pushToMyTracker = (payload: TmrPayload): void => {
  try {
    const tmr = (window._tmr ??= []);
    tmr.push(payload);
  } catch {
    console.error('Error pushing to MyTracker', payload);
  }
};

export const myTracker = {
  setUserId(userId: string): void {
    pushToMyTracker({
      id: MYTRACKER_COUNTER_ID,
      type: 'setUserID',
      params: {
        userid: userId,
      },
    });
  },
  clearUserId(): void {
    pushToMyTracker({ type: 'deleteUserID' });
  },
  trackRegistration(userId: string, email: string | null): void {
    pushToMyTracker({
      id: MYTRACKER_COUNTER_ID,
      type: 'reachGoal',
      goal: 'mt_registration',
      params: {
        userid: userId,
        email: email,
      },
    });
  },
  trackLogin(userId: string, email: string | null): void {
    pushToMyTracker({
      id: MYTRACKER_COUNTER_ID,
      type: 'reachGoal',
      goal: 'mt_login',
      params: {
        userid: userId,
        email: email,
      },
    });
  },
  trackProductLinkClicked(
    item: CatalogItem,
    source: TSourceTrackPageType
  ): void {
    pushToMyTracker({
      id: MYTRACKER_COUNTER_ID,
      type: 'reachGoal',
      goal: 'product_link_clicked',
      typeEvent: 'conversion',
      params: {
        item_id: item.id,
        brand_id: item.shopName,
        url: item.url,
        price: item.price,
        category: item.categoryType,
        source: source,
      },
    });
  },
  trackProductViewed(
    item: CatalogItem,
    source: TSourceTrackPageType,
    positionInFeed: TPositionInFeed
  ): void {
    pushToMyTracker({
      id: MYTRACKER_COUNTER_ID,
      type: 'reachGoal',
      typeEvent: 'action',
      goal: 'product_viewed',
      params: {
        item_id: item.id,
        brand_id: item.shopName,
        price: item.price,
        category: item.categoryType,
        source: source,
        position_in_feed: positionInFeed,
      },
    });
  },
  trackBrandItemShownInChat(
    item: CatalogItem,
    responseType: TChatResponseType,
    positionInFeed: TPositionInFeed,
    sessionId: string
  ): void {
    pushToMyTracker({
      id: MYTRACKER_COUNTER_ID,
      type: 'reachGoal',
      typeEvent: 'action',
      goal: 'brand_item_shown_in_chat',
      params: {
        brand_id: item.shopName,
        brand_name: item.shopName,
        item_id: item.id,
        item_name: item.name,
        position_in_response: positionInFeed,
        response_type: responseType,
        session_id: sessionId,
      },
    });
  },
  trackPaywallViewed({
    trigger,
    featureBlocked,
  }: {
    trigger: PaywallTrigger;
    featureBlocked?: string;
  }): void {
    pushToMyTracker({
      id: MYTRACKER_COUNTER_ID,
      type: 'reachGoal',
      typeEvent: 'screen_view',
      goal: 'paywall_viewed',
      params: {
        trigger,
        ...(featureBlocked ? { feature_blocked: featureBlocked } : {}),
      },
    });
  },
  trackSubscriptionPlanSelected({
    plan,
    billingPeriod,
  }: {
    plan: SubscriptionPlan;
    billingPeriod: BillingPeriod;
  }): void {
    pushToMyTracker({
      id: MYTRACKER_COUNTER_ID,
      type: 'reachGoal',
      typeEvent: 'action',
      goal: 'subscription_plan_selected',
      params: {
        plan,
        billing_period: billingPeriod,
      },
    });
  },
  trackSubscriptionStart({
    plan,
    billingPeriod,
    amount,
  }: {
    plan: SubscriptionPlan;
    billingPeriod: BillingPeriod;
    amount: number;
  }): void {
    pushToMyTracker({
      id: MYTRACKER_COUNTER_ID,
      type: 'reachGoal',
      typeEvent: 'conversion',
      goal: 'subscription_start',
      params: {
        plan,
        billing_period: billingPeriod,
        amount,
      },
    });
  },
  trackSubscriptionCancelled({ plan }: { plan: string }): void {
    pushToMyTracker({
      id: MYTRACKER_COUNTER_ID,
      type: 'reachGoal',
      typeEvent: 'action',
      goal: 'subscription_cancelled',
      params: {
        plan,
      },
    });
  },
  trackWardrobeOpen({
    tab,
    userId,
  }: {
    tab: WardrobeTab;
    userId: string;
  }): void {
    pushToMyTracker({
      id: MYTRACKER_COUNTER_ID,
      type: 'reachGoal',
      typeEvent: 'screen_view',
      goal: 'wardrobe_open',
      params: {
        tab,
        user_id: userId,
      },
    });
  },
  trackItemDigitizeStart({
    method,
    category,
  }: {
    method: DigitizeMethod;
    category?: string;
  }): void {
    pushToMyTracker({
      id: MYTRACKER_COUNTER_ID,
      type: 'reachGoal',
      typeEvent: 'action',
      goal: 'item_digitize_start',
      params: {
        method,
        ...(category ? { category } : {}),
      },
    });
  },
  trackItemDigitizeComplete({
    itemId,
    category,
    bgRemoved,
    timeSpentSec,
  }: {
    itemId: string;
    category: string;
    bgRemoved: boolean;
    timeSpentSec: number;
  }): void {
    pushToMyTracker({
      id: MYTRACKER_COUNTER_ID,
      type: 'reachGoal',
      typeEvent: 'conversion',
      goal: 'item_digitize_complete',
      params: {
        item_id: itemId,
        category,
        bg_removed: bgRemoved,
        time_spent_sec: timeSpentSec,
      },
    });
  },
  trackItemDigitizeFail({
    category,
    errorType,
    retried,
  }: {
    category?: string;
    errorType: DigitizeErrorType;
    retried: boolean;
  }): void {
    pushToMyTracker({
      id: MYTRACKER_COUNTER_ID,
      type: 'reachGoal',
      typeEvent: 'action',
      goal: 'item_digitize_fail',
      params: {
        error_type: errorType,
        retried,
        ...(category ? { category } : {}),
      },
    });
  },
  trackEnhanceItem({
    itemId,
    category,
    enhancementType = 'full',
  }: {
    itemId: string;
    category: string;
    enhancementType?: EnhancementType;
  }): void {
    pushToMyTracker({
      id: MYTRACKER_COUNTER_ID,
      type: 'reachGoal',
      typeEvent: 'action',
      goal: 'enhance_item',
      params: {
        item_id: itemId,
        category,
        enhancement_type: enhancementType,
      },
    });
  },
  trackEnhanceItemComplete({
    itemId,
    category,
    enhancementType = 'full',
  }: {
    itemId: string;
    category: string;
    enhancementType?: EnhancementType;
  }): void {
    pushToMyTracker({
      id: MYTRACKER_COUNTER_ID,
      type: 'reachGoal',
      typeEvent: 'action',
      goal: 'enhance_item_complete',
      params: {
        item_id: itemId,
        category,
        enhancement_type: enhancementType,
      },
    });
  },
  trackEnhanceItemRejected({
    itemId,
    category,
    enhancementType = 'full',
    rejectReason,
  }: {
    itemId: string;
    category: string;
    enhancementType?: EnhancementType;
    rejectReason: EnhanceRejectReason;
  }): void {
    pushToMyTracker({
      id: MYTRACKER_COUNTER_ID,
      type: 'reachGoal',
      typeEvent: 'action',
      goal: 'enhance_item_rejected',
      params: {
        item_id: itemId,
        category,
        enhancement_type: enhancementType,
        reject_reason: rejectReason,
      },
    });
  },
  trackWardrobeItemDeleted({
    itemId,
    category,
    reason = 'user_action',
  }: {
    itemId: string;
    category: string;
    reason?: 'user_action';
  }): void {
    pushToMyTracker({
      id: MYTRACKER_COUNTER_ID,
      type: 'reachGoal',
      typeEvent: 'action',
      goal: 'wardrobe_item_deleted',
      params: {
        item_id: itemId,
        category,
        reason,
      },
    });
  },
  trackOnboardingStart({
    step,
    onboardingVersion,
  }: {
    step: number;
    onboardingVersion: string;
  }): void {
    pushToMyTracker({
      id: MYTRACKER_COUNTER_ID,
      type: 'reachGoal',
      typeEvent: 'screen_view',
      goal: 'onboarding_start',
      params: {
        step,
        onboarding_version: onboardingVersion,
      },
    });
  },
  trackOnboardingStepComplete({
    step,
    stepName,
    skipped,
  }: {
    step: number;
    stepName: OnboardingStepName;
    skipped: boolean;
  }): void {
    pushToMyTracker({
      id: MYTRACKER_COUNTER_ID,
      type: 'reachGoal',
      typeEvent: 'action',
      goal: 'onboarding_step_complete',
      params: {
        step,
        step_name: stepName,
        skipped,
      },
    });
  },
  trackOnboardingComplete({
    stepsCompleted,
    timeSpentSec,
    skippedAny,
  }: {
    stepsCompleted: number;
    timeSpentSec: number;
    skippedAny: boolean;
  }): void {
    pushToMyTracker({
      id: MYTRACKER_COUNTER_ID,
      type: 'reachGoal',
      typeEvent: 'conversion',
      goal: 'onboarding_complete',
      params: {
        steps_completed: stepsCompleted,
        time_spent_sec: timeSpentSec,
        skipped_any: skippedAny,
      },
    });
  },
  trackOnboardingSkip({
    skippedAtStep,
    reason,
  }: {
    skippedAtStep: number;
    reason?: string;
  }): void {
    pushToMyTracker({
      id: MYTRACKER_COUNTER_ID,
      type: 'reachGoal',
      typeEvent: 'action',
      goal: 'onboarding_skip',
      params: {
        skipped_at_step: skippedAtStep,
        ...(reason ? { reason } : {}),
      },
    });
  },
  trackChatOutfitSaved({
    outfitId,
    itemsInOutfit,
  }: {
    outfitId: string;
    itemsInOutfit: number;
  }): void {
    pushToMyTracker({
      id: MYTRACKER_COUNTER_ID,
      type: 'reachGoal',
      typeEvent: 'conversion',
      goal: 'chat_outfit_saved',
      params: {
        outfit_id: outfitId,
        source: 'chat_response',
        items_in_outfit: itemsInOutfit,
      },
    });
  },
  trackChatItemSaved({
    itemId,
    brandId,
    category,
  }: {
    itemId: string;
    brandId: string;
    category: string;
  }): void {
    pushToMyTracker({
      id: MYTRACKER_COUNTER_ID,
      type: 'reachGoal',
      typeEvent: 'conversion',
      goal: 'chat_item_saved',
      params: {
        item_id: itemId,
        brand_id: brandId,
        category,
        source: 'chat',
      },
    });
  },
  trackChatLinkClicked({
    brandId,
    itemId,
    url,
    positionInResponse,
  }: {
    brandId: string;
    itemId: string;
    url: string;
    positionInResponse: number;
  }): void {
    pushToMyTracker({
      id: MYTRACKER_COUNTER_ID,
      type: 'reachGoal',
      typeEvent: 'conversion',
      goal: 'chat_link_clicked',
      params: {
        brand_id: brandId,
        item_id: itemId,
        url,
        position_in_response: positionInResponse,
      },
    });
  },
  trackChatMessageSent({
    sessionId,
    messageLength,
    hasAttachment,
    sessionMessageNum,
  }: {
    sessionId: string;
    messageLength: number;
    hasAttachment: boolean;
    sessionMessageNum: number;
  }): void {
    pushToMyTracker({
      id: MYTRACKER_COUNTER_ID,
      type: 'reachGoal',
      typeEvent: 'action',
      goal: 'chat_message_sent',
      params: {
        session_id: sessionId,
        message_length: messageLength,
        has_attachment: hasAttachment,
        session_message_num: sessionMessageNum,
      },
    });
  },
  trackChatQuickActionClick({
    sessionId,
    actionText,
  }: {
    sessionId: string;
    actionText: string;
  }): void {
    pushToMyTracker({
      id: MYTRACKER_COUNTER_ID,
      type: 'reachGoal',
      typeEvent: 'click',
      goal: 'chat_quick_action_click',
      params: {
        session_id: sessionId,
        action_text: actionText,
      },
    });
  },
  trackQuickMenuClick({
    sessionId,
    menuAction,
  }: {
    sessionId: string;
    menuAction: QuickMenuAction;
  }): void {
    pushToMyTracker({
      id: MYTRACKER_COUNTER_ID,
      type: 'reachGoal',
      typeEvent: 'click',
      goal: 'quick_menu_click',
      params: {
        session_id: sessionId,
        menu_action: menuAction,
      },
    });
  },
  trackChatImageAttached({
    sessionId,
    fileType,
    useCase,
  }: {
    sessionId: string;
    fileType: string;
    useCase?: string;
  }): void {
    pushToMyTracker({
      id: MYTRACKER_COUNTER_ID,
      type: 'reachGoal',
      typeEvent: 'action',
      goal: 'chat_image_attached',
      params: {
        session_id: sessionId,
        file_type: fileType,
        ...(useCase ? { use_case: useCase } : {}),
      },
    });
  },
  trackChatResponseReceived({
    sessionId,
    responseTimeMs,
    hasItems,
    responseType,
  }: {
    sessionId: string;
    responseTimeMs: number;
    hasItems: boolean;
    responseType: string;
  }): void {
    pushToMyTracker({
      id: MYTRACKER_COUNTER_ID,
      type: 'reachGoal',
      typeEvent: 'action',
      goal: 'chat_response_received',
      params: {
        session_id: sessionId,
        response_time_ms: responseTimeMs,
        has_items: hasItems,
        response_type: responseType,
      },
    });
  },
  trackObrazGenerated({
    sessionId,
    itemsUsed,
  }: {
    sessionId: string;
    itemsUsed: number;
  }): void {
    pushToMyTracker({
      id: MYTRACKER_COUNTER_ID,
      type: 'reachGoal',
      typeEvent: 'conversion',
      goal: 'obraz_generated',
      params: {
        session_id: sessionId,
        items_used: itemsUsed,
      },
    });
  },
  trackWardrobeObrazGenerated({
    sessionId,
    itemsUsed,
    itemsTotalInWardrobe,
  }: {
    sessionId: string;
    itemsUsed: number;
    itemsTotalInWardrobe: number;
  }): void {
    pushToMyTracker({
      id: MYTRACKER_COUNTER_ID,
      type: 'reachGoal',
      typeEvent: 'conversion',
      goal: 'wardrobe_obraz_generated',
      params: {
        session_id: sessionId,
        items_used: itemsUsed,
        items_total_in_wardrobe: itemsTotalInWardrobe,
      },
    });
  },
  trackCapsuleGenerated({
    sessionId,
    itemsUsed,
  }: {
    sessionId: string;
    itemsUsed: number;
  }): void {
    pushToMyTracker({
      id: MYTRACKER_COUNTER_ID,
      type: 'reachGoal',
      typeEvent: 'conversion',
      goal: 'capsule_generated',
      params: {
        session_id: sessionId,
        items_used: itemsUsed,
      },
    });
  },
  trackWardrobeCapsuleGenerated({
    sessionId,
    itemsUsed,
    itemsTotalInWardrobe,
  }: {
    sessionId: string;
    itemsUsed: number;
    itemsTotalInWardrobe: number;
  }): void {
    pushToMyTracker({
      id: MYTRACKER_COUNTER_ID,
      type: 'reachGoal',
      typeEvent: 'conversion',
      goal: 'wardrobe_capsule_generated',
      params: {
        session_id: sessionId,
        items_used: itemsUsed,
        items_total_in_wardrobe: itemsTotalInWardrobe,
      },
    });
  },
  trackChatSearch({
    sessionId,
    itemsFound,
  }: {
    sessionId: string;
    itemsFound?: number;
  }): void {
    pushToMyTracker({
      id: MYTRACKER_COUNTER_ID,
      type: 'reachGoal',
      typeEvent: 'conversion',
      goal: 'search',
      params: {
        session_id: sessionId,
        ...(itemsFound != null ? { items_found: itemsFound } : {}),
      },
    });
  },
};
